import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import {appointmentService} from '../../services/appointmentService';

const {FiPlus,FiEdit2,FiTrash2,FiMapPin,FiClock,FiCalendar,FiDollarSign,FiInfo,FiCheck,FiX,FiLoader,FiAlertCircle,FiGrid,FiList,FiRefreshCw,FiWifi,FiWifiOff,FiToggleLeft,FiToggleRight,FiEye,FiEyeOff,FiBarChart3}=FiIcons;

const AppointmentManagement=()=> {
  const [appointments,setAppointments]=useState([]);
  const [loading,setLoading]=useState(true);
  const [showModal,setShowModal]=useState(false);
  const [editingAppointment,setEditingAppointment]=useState(null);
  const [submitting,setSubmitting]=useState(false);
  const [error,setError]=useState('');
  const [success,setSuccess]=useState('');
  const [connectionStatus,setConnectionStatus]=useState('checking');
  const [viewMode,setViewMode]=useState('grid');// grid or list
  const [filterStatus,setFilterStatus]=useState('all');// all,enabled,disabled
  const [stats,setStats]=useState({
    totalTypes: 0,
    enabledTypes: 0,
    disabledTypes: 0
  });

  const [formData,setFormData]=useState({
    name: '',
    availableDays: [],
    consultationFee: '',
    duration: 5,
    selectedTimeSlots: [],
    location: '',
    googleMapsUrl: '',
    additionalInfo: '',
    isEnabled: true
  });

  const daysOfWeek=['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  // Predefined duration options
  const durationOptions=[
    {value: 5,label: '5 minutes',description: 'Quick check-up'},
    {value: 10,label: '10 minutes',description: 'Brief consultation'},
    {value: 15,label: '15 minutes',description: 'Standard consultation'}
  ];

  // Test connection and load appointments on component mount
  useEffect(()=> {
    testConnectionAndLoad();
  },[]);

  const testConnectionAndLoad=async ()=> {
    setConnectionStatus('checking');
    try {
      // Initialize table first
      await appointmentService.initializeTable();
      
      // Test connection
      const connectionResult=await appointmentService.testConnection();
      if (connectionResult.success) {
        setConnectionStatus('connected');
        await loadAppointments();
        await loadStats();
      } else {
        setConnectionStatus('failed');
        setError(`Connection failed: ${connectionResult.error}`);
      }
    } catch (error) {
      console.error('Connection test failed:',error);
      setConnectionStatus('failed');
      setError('Unable to connect to database');
    }
  };

  const loadAppointments=async ()=> {
    setLoading(true);
    setError('');
    try {
      console.log('Loading appointments...');
      const result=await appointmentService.getAppointmentTypes();
      if (result.success) {
        console.log('Loaded appointments:',result.data);
        setAppointments(result.data || []);
      } else {
        console.error('Failed to load appointments:',result.error);
        setError(result.error || 'Failed to load appointments');
      }
    } catch (error) {
      console.error('Error loading appointments:',error);
      setError('An unexpected error occurred while loading appointments');
    } finally {
      setLoading(false);
    }
  };

  const loadStats=async ()=> {
    try {
      const result=await appointmentService.getAppointmentStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (error) {
      console.error('Error loading stats:',error);
    }
  };

  // Toggle appointment type status
  const handleToggleStatus=async (id,currentStatus)=> {
    try {
      const newStatus=!currentStatus;
      const result=await appointmentService.toggleAppointmentTypeStatus(id,newStatus);
      
      if (result.success) {
        setSuccess(`Appointment type ${newStatus ? 'enabled' : 'disabled'} successfully!`);
        await loadAppointments();
        await loadStats();
        
        // Clear success message after 3 seconds
        setTimeout(()=> setSuccess(''),3000);
      } else {
        setError(result.error || 'Failed to update appointment status');
      }
    } catch (error) {
      console.error('Error toggling status:',error);
      setError('An unexpected error occurred while updating status');
    }
  };

  // Filter appointments based on status
  const filteredAppointments=appointments.filter(appointment=> {
    if (filterStatus==='all') return true;
    if (filterStatus==='enabled') return appointment.is_enabled !==false;// Default to true if undefined
    if (filterStatus==='disabled') return appointment.is_enabled===false;
    return true;
  });

  // Convert 24-hour time to 12-hour format
  const formatTimeTo12Hour=(time24)=> {
    if (!time24) return '';
    
    const [hours,minutes]=time24.split(':');
    const hour24=parseInt(hours,10);
    const min=parseInt(minutes,10);
    
    const hour12=hour24===0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm=hour24 >=12 ? 'PM' : 'AM';
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Convert 12-hour time to 24-hour format
  const formatTimeTo24Hour=(time12)=> {
    if (!time12) return '';
    
    const timeRegex=/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
    const match=time12.match(timeRegex);
    
    if (!match) return time12; // Return as-is if format doesn't match
    
    let [,hours,minutes,ampm]=match;
    hours=parseInt(hours,10);
    
    if (ampm.toUpperCase()==='AM') {
      if (hours===12) hours=0;
    } else {
      if (hours !==12) hours +=12;
    }
    
    return `${hours.toString().padStart(2,'0')}:${minutes}`;
  };

  // Generate all possible time slots from 9 AM to 9 PM based on duration
  const generateAllTimeSlots=(duration)=> {
    const slots=[];
    const startTime='09:00';
    const endTime='21:00';
    
    const start=new Date(`1970-01-01T${startTime}:00`);
    const end=new Date(`1970-01-01T${endTime}:00`);
    const durationMs=duration * 60 * 1000;
    
    let current=new Date(start);
    
    while (current < end) {
      const next=new Date(current.getTime() + durationMs);
      if (next <=end) {
        const startStr=current.toTimeString().slice(0,5);
        const endStr=next.toTimeString().slice(0,5);
        
        // Format to 12-hour for display
        const startStr12=formatTimeTo12Hour(startStr);
        const endStr12=formatTimeTo12Hour(endStr);
        
        slots.push({
          id: `${startStr}-${endStr}`,
          startTime: startStr,
          endTime: endStr,
          label: `${startStr12} - ${endStr12}`,
          value: `${startStr}-${endStr}`, // Store in 24-hour format
          display: `${startStr12} - ${endStr12}` // 12-hour format for display
        });
      }
      current=next;
    }
    
    return slots;
  };

  // Get available time slots based on selected duration
  const [availableTimeSlots,setAvailableTimeSlots]=useState([]);

  useEffect(()=> {
    if (formData.duration) {
      const slots=generateAllTimeSlots(parseInt(formData.duration));
      setAvailableTimeSlots(slots);
    }
  },[formData.duration]);

  // Handle time slot selection/deselection
  const handleTimeSlotToggle=(slotValue)=> {
    setFormData(prev=> ({
      ...prev,
      selectedTimeSlots: prev.selectedTimeSlots.includes(slotValue)
        ? prev.selectedTimeSlots.filter(slot=> slot !==slotValue)
        : [...prev.selectedTimeSlots,slotValue]
    }));
  };

  // Select all time slots
  const handleSelectAllTimeSlots=()=> {
    const allSlotValues=availableTimeSlots.map(slot=> slot.value);
    setFormData(prev=> ({
      ...prev,
      selectedTimeSlots: allSlotValues
    }));
  };

  // Clear all time slots
  const handleClearAllTimeSlots=()=> {
    setFormData(prev=> ({
      ...prev,
      selectedTimeSlots: []
    }));
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    
    // Validation
    if (formData.selectedTimeSlots.length===0) {
      setError('Please select at least one time slot');
      return;
    }
    
    if (formData.availableDays.length===0) {
      setError('Please select at least one available day');
      return;
    }
    
    if (!formData.name.trim()) {
      setError('Please enter an appointment type name');
      return;
    }
    
    if (!formData.consultationFee || parseInt(formData.consultationFee) <=0) {
      setError('Please enter a valid consultation fee');
      return;
    }
    
    if (!formData.location.trim()) {
      setError('Please enter a location');
      return;
    }
    
    // NEW MANDATORY VALIDATIONS
    if (!formData.googleMapsUrl.trim()) {
      setError('Please enter a Google Maps Embed URL');
      return;
    }
    
    if (!formData.additionalInfo.trim()) {
      setError('Please enter additional information');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const appointmentData={
        name: formData.name.trim(),
        availableDays: formData.availableDays,
        consultationFee: parseInt(formData.consultationFee),
        duration: parseInt(formData.duration),
        timeSlots: formData.selectedTimeSlots, // Already in 24-hour format
        location: formData.location.trim(),
        googleMapsUrl: formData.googleMapsUrl.trim(),
        additionalInfo: formData.additionalInfo.trim(),
        isEnabled: formData.isEnabled
      };

      console.log('Submitting appointment data:',appointmentData);

      let result;
      if (editingAppointment) {
        console.log('Updating appointment with ID:',editingAppointment.id);
        result=await appointmentService.updateAppointmentType(editingAppointment.id,appointmentData);
      } else {
        console.log('Creating new appointment');
        result=await appointmentService.createAppointmentType(appointmentData);
      }

      console.log('Submit result:',result);

      if (result.success) {
        setSuccess(editingAppointment ? 'Appointment type updated successfully!' : 'Appointment type created successfully!');
        await loadAppointments();
        await loadStats();
        resetForm();
        
        // Clear success message after 3 seconds
        setTimeout(()=> setSuccess(''),3000);
      } else {
        console.error('Submit failed:',result.error);
        setError(result.error || 'Failed to save appointment type');
      }
    } catch (error) {
      console.error('Error saving appointment:',error);
      setError('An unexpected error occurred while saving');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm=()=> {
    setFormData({
      name: '',
      availableDays: [],
      consultationFee: '',
      duration: 5,
      selectedTimeSlots: [],
      location: '',
      googleMapsUrl: '',
      additionalInfo: '',
      isEnabled: true
    });
    setEditingAppointment(null);
    setShowModal(false);
    setError('');
    setSuccess('');
  };

  const handleEdit=(appointment)=> {
    console.log('Editing appointment:',appointment);
    setFormData({
      name: appointment.name || '',
      availableDays: appointment.available_days || [],
      consultationFee: appointment.consultation_fee?.toString() || '',
      duration: appointment.duration || 5,
      selectedTimeSlots: appointment.time_slots || [], // These are already in 24-hour format
      location: appointment.location || '',
      googleMapsUrl: appointment.google_maps_url || '',
      additionalInfo: appointment.additional_info || '',
      isEnabled: appointment.is_enabled !==false // Default to true if undefined
    });
    setEditingAppointment(appointment);
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  const handleDelete=async (id)=> {
    if (window.confirm('Are you sure you want to delete this appointment type?')) {
      setError('');
      try {
        console.log('Deleting appointment with ID:',id);
        const result=await appointmentService.deleteAppointmentType(id);
        
        if (result.success) {
          setSuccess('Appointment type deleted successfully!');
          await loadAppointments();
          await loadStats();
          
          // Clear success message after 3 seconds
          setTimeout(()=> setSuccess(''),3000);
        } else {
          console.error('Delete failed:',result.error);
          setError(result.error || 'Failed to delete appointment type');
        }
      } catch (error) {
        console.error('Error deleting appointment:',error);
        setError('An unexpected error occurred while deleting');
      }
    }
  };

  const handleDayToggle=(day)=> {
    setFormData({
      ...formData,
      availableDays: formData.availableDays.includes(day)
        ? formData.availableDays.filter(d=> d !==day)
        : [...formData.availableDays,day]
    });
  };

  const getDurationLabel=(duration)=> {
    const option=durationOptions.find(opt=> opt.value===duration);
    return option ? option.label : `${duration} minutes`;
  };

  // Group time slots by time periods for better display
  const groupTimeSlotsByPeriod=(slots)=> {
    const morning=slots.filter(slot=> {
      const hour=parseInt(slot.startTime.split(':')[0]);
      return hour >=9 && hour < 12;
    });

    const afternoon=slots.filter(slot=> {
      const hour=parseInt(slot.startTime.split(':')[0]);
      return hour >=12 && hour < 17;
    });

    const evening=slots.filter(slot=> {
      const hour=parseInt(slot.startTime.split(':')[0]);
      return hour >=17 && hour < 21;
    });

    return {morning,afternoon,evening};
  };

  // Convert time slot from 24-hour to 12-hour format for display
  const convertTimeSlotToDisplay=(slot)=> {
    if (!slot) return '';
    
    // Check if it's already in display format (contains AM/PM)
    if (slot.includes('AM') || slot.includes('PM')) {
      return slot;
    }
    
    // Parse 24-hour format: "09:00-09:05"
    const parts=slot.split('-');
    if (parts.length===2) {
      const startTime12=formatTimeTo12Hour(parts[0]);
      const endTime12=formatTimeTo12Hour(parts[1]);
      return `${startTime12} - ${endTime12}`;
    }
    
    return slot;
  };

  // Show connection status
  if (connectionStatus==='checking') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Connecting to database...</p>
        </div>
      </div>
    );
  }

  if (connectionStatus==='failed') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <SafeIcon icon={FiAlertCircle} className="h-16 w-16 text-red-600 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Database Connection Failed</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={testConnectionAndLoad}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <SafeIcon icon={FiLoader} className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
                <SafeIcon icon={FiCalendar} className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Appointment Management</h1>
                <p className="text-gray-600 mt-1">Manage appointment types,schedules,time slots,and availability for Best Podiatrist</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Connection Status */}
              <div className={`flex items-center px-3 py-2 rounded-full text-sm font-medium ${
                connectionStatus==='connected' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-red-100 text-red-700'
              }`}>
                <SafeIcon icon={connectionStatus==='connected' ? FiWifi : FiWifiOff} className="h-4 w-4 mr-2" />
                {connectionStatus==='connected' ? 'Connected' : 'Disconnected'}
              </div>

              {/* View Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={()=> setViewMode('grid')}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode==='grid' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <SafeIcon icon={FiGrid} className="h-4 w-4 mr-2" />
                  Grid
                </button>
                <button
                  onClick={()=> setViewMode('list')}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode==='list' 
                      ? 'bg-white text-gray-900 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <SafeIcon icon={FiList} className="h-4 w-4 mr-2" />
                  List
                </button>
              </div>

              {/* Refresh Button */}
              <motion.button
                onClick={()=> {loadAppointments();loadStats();}}
                disabled={loading}
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh appointments"
              >
                <SafeIcon icon={FiRefreshCw} className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </motion.button>

              {/* Add Button */}
              <motion.button
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                onClick={()=> setShowModal(true)}
                className="flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all shadow-sm"
              >
                <SafeIcon icon={FiPlus} className="mr-2 h-5 w-5" />
                Add Appointment Type
              </motion.button>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiBarChart3} className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Types</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalTypes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiEye} className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Enabled</p>
                <p className="text-2xl font-bold text-green-600">{stats.enabledTypes}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiEyeOff} className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Disabled</p>
                <p className="text-2xl font-bold text-red-600">{stats.disabledTypes}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              onClick={()=> setFilterStatus('all')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filterStatus==='all' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              All ({appointments.length})
            </button>
            <button
              onClick={()=> setFilterStatus('enabled')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filterStatus==='enabled' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Enabled ({stats.enabledTypes})
            </button>
            <button
              onClick={()=> setFilterStatus('disabled')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                filterStatus==='disabled' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Disabled ({stats.disabledTypes})
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {(success || error) && (
          <motion.div
            initial={{opacity: 0,y: -10}}
            animate={{opacity: 1,y: 0}}
            className={`mb-6 p-4 rounded-xl border ${
              success 
                ? 'bg-green-50 border-green-200 text-green-700' 
                : 'bg-red-50 border-red-200 text-red-700'
            }`}
          >
            <div className="flex items-center">
              <SafeIcon icon={success ? FiCheck : FiAlertCircle} className="h-5 w-5 mr-3" />
              <span className="font-medium">{success || error}</span>
            </div>
          </motion.div>
        )}

        {/* Appointments Display */}
        {filteredAppointments.length===0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 text-center py-16">
            <SafeIcon icon={FiCalendar} className="h-16 w-16 text-gray-300 mx-auto mb-6" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              {filterStatus==='all' 
                ? 'No appointment types found' 
                : `No ${filterStatus} appointment types found`}
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {filterStatus==='all' 
                ? 'Get started by creating your first appointment type for Best Podiatrist practice.'
                : `Try switching to a different filter or create a new appointment type.`}
            </p>
            {filterStatus==='all' && (
              <motion.button
                whileHover={{scale: 1.02}}
                whileTap={{scale: 0.98}}
                onClick={()=> setShowModal(true)}
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-cyan-700 shadow-sm"
              >
                <SafeIcon icon={FiPlus} className="mr-2 h-5 w-5" />
                Add Your First Appointment Type
              </motion.button>
            )}
          </div>
        ) : (
          <div className={`${
            viewMode==='grid' 
              ? 'grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-6' 
              : 'space-y-4'
          }`}>
            {filteredAppointments.map((appointment,index)=> (
              <motion.div
                key={appointment.id}
                initial={{opacity: 0,y: 20}}
                animate={{opacity: 1,y: 0}}
                transition={{delay: index * 0.1}}
                className={`bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200 ${
                  viewMode==='list' ? 'flex items-center' : ''
                } ${
                  appointment.is_enabled===false 
                    ? 'opacity-75 border-red-200 bg-gray-50' 
                    : ''
                }`}
              >
                {viewMode==='grid' ? (
                  // Grid View
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center space-x-3">
                        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
                          appointment.is_enabled===false 
                            ? 'bg-gray-300' 
                            : 'bg-gradient-to-r from-blue-600 to-cyan-600'
                        }`}>
                          <SafeIcon icon={FiCalendar} className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{appointment.name}</h3>
                          <div className="flex items-center mt-1">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              appointment.is_enabled===false 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              <SafeIcon icon={appointment.is_enabled===false ? FiEyeOff : FiEye} className="w-3 h-3 mr-1" />
                              {appointment.is_enabled===false ? 'Disabled' : 'Enabled'}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        {/* Enable/Disable Toggle */}
                        <motion.button
                          whileHover={{scale: 1.05}}
                          whileTap={{scale: 0.95}}
                          onClick={()=> handleToggleStatus(appointment.id,appointment.is_enabled !==false)}
                          className={`p-2 rounded-lg transition-colors ${
                            appointment.is_enabled===false 
                              ? 'text-red-600 hover:bg-red-50' 
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={appointment.is_enabled===false ? 'Enable appointment type' : 'Disable appointment type'}
                        >
                          <SafeIcon icon={appointment.is_enabled===false ? FiToggleLeft : FiToggleRight} className="h-5 w-5" />
                        </motion.button>

                        <button
                          onClick={()=> handleEdit(appointment)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit appointment"
                        >
                          <SafeIcon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={()=> handleDelete(appointment.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete appointment"
                        >
                          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiCalendar} className="mr-3 h-4 w-4 text-blue-500" />
                        <span className="font-medium mr-2">Days:</span>
                        <span>{appointment.available_days?.join(',') || 'No days specified'}</span>
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiDollarSign} className="mr-3 h-4 w-4 text-green-500" />
                        <span className="font-medium mr-2">Fee:</span>
                        <span className="font-semibold text-green-600">₹{appointment.consultation_fee}</span>
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <SafeIcon icon={FiClock} className="mr-3 h-4 w-4 text-purple-500" />
                        <span className="font-medium mr-2">Duration:</span>
                        <span>{getDurationLabel(appointment.duration)}</span>
                      </div>

                      <div className="flex items-start text-sm text-gray-600">
                        <SafeIcon icon={FiMapPin} className="mr-3 h-4 w-4 mt-0.5 text-red-500" />
                        <div>
                          <span className="font-medium">Location:</span>
                          <p className="text-gray-700 mt-1">{appointment.location}</p>
                        </div>
                      </div>

                      {appointment.additional_info && (
                        <div className="flex items-start text-sm text-gray-600">
                          <SafeIcon icon={FiInfo} className="mr-3 h-4 w-4 mt-0.5 text-orange-500" />
                          <div>
                            <span className="font-medium">Notes:</span>
                            <p className="text-gray-700 mt-1">{appointment.additional_info}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Time Slots Preview */}
                    <div className="border-t border-gray-100 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Time Slots ({appointment.time_slots?.length || 0} available)
                      </h4>
                      <div className="grid grid-cols-1 gap-2 max-h-24 overflow-y-auto">
                        {appointment.time_slots?.slice(0,6).map((slot)=> (
                          <div
                            key={slot}
                            className={`px-2 py-1 border rounded text-xs text-center font-medium ${
                              appointment.is_enabled===false 
                                ? 'bg-gray-100 border-gray-300 text-gray-500' 
                                : 'bg-blue-50 border-blue-200 text-blue-700'
                            }`}
                          >
                            {convertTimeSlotToDisplay(slot)}
                          </div>
                        ))}
                        {(appointment.time_slots?.length || 0) > 6 && (
                          <div className="px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-500 text-center">
                            +{(appointment.time_slots?.length || 0) - 6} more slots
                          </div>
                        )}
                      </div>
                    </div>

                    {appointment.google_maps_url && (
                      <div className="mt-4 border-t border-gray-100 pt-4">
                        <iframe
                          src={appointment.google_maps_url}
                          width="100%"
                          height="120"
                          style={{border: 0}}
                          allowFullScreen=""
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                          className="rounded-lg"
                        ></iframe>
                      </div>
                    )}
                  </div>
                ) : (
                  // List View
                  <div className="flex-1 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                          appointment.is_enabled===false 
                            ? 'bg-gray-300' 
                            : 'bg-gradient-to-r from-blue-600 to-cyan-600'
                        }`}>
                          <SafeIcon icon={FiCalendar} className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3">
                            <h3 className="text-lg font-semibold text-gray-900">{appointment.name}</h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              appointment.is_enabled===false 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-green-100 text-green-800'
                            }`}>
                              <SafeIcon icon={appointment.is_enabled===false ? FiEyeOff : FiEye} className="w-3 h-3 mr-1" />
                              {appointment.is_enabled===false ? 'Disabled' : 'Enabled'}
                            </span>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                            <span>₹{appointment.consultation_fee}</span>
                            <span>•</span>
                            <span>{getDurationLabel(appointment.duration)}</span>
                            <span>•</span>
                            <span>{appointment.time_slots?.length || 0} slots</span>
                            <span>•</span>
                            <span>{appointment.available_days?.join(',') || 'No days'}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2">
                        {/* Enable/Disable Toggle */}
                        <motion.button
                          whileHover={{scale: 1.05}}
                          whileTap={{scale: 0.95}}
                          onClick={()=> handleToggleStatus(appointment.id,appointment.is_enabled !==false)}
                          className={`p-2 rounded-lg transition-colors ${
                            appointment.is_enabled===false 
                              ? 'text-red-600 hover:bg-red-50' 
                              : 'text-green-600 hover:bg-green-50'
                          }`}
                          title={appointment.is_enabled===false ? 'Enable appointment type' : 'Disable appointment type'}
                        >
                          <SafeIcon icon={appointment.is_enabled===false ? FiToggleLeft : FiToggleRight} className="h-5 w-5" />
                        </motion.button>

                        <button
                          onClick={()=> handleEdit(appointment)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit appointment"
                        >
                          <SafeIcon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                        <button
                          onClick={()=> handleDelete(appointment.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete appointment"
                        >
                          <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}

        {/* Modal - Enhanced for better width/height management */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{opacity: 0,scale: 0.95}}
              animate={{opacity: 1,scale: 1}}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden"
            >
              <div className="flex flex-col h-full max-h-[95vh]">
                {/* Modal Header */}
                <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-cyan-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiCalendar} className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">
                        {editingAppointment ? 'Edit Appointment Type' : 'Add New Appointment Type'}
                      </h3>
                    </div>
                    <button
                      onClick={resetForm}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
                    >
                      <SafeIcon icon={FiX} className="h-6 w-6" />
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="flex-1 overflow-y-auto">
                  <form onSubmit={handleSubmit} className="p-8">
                    {/* Error Display */}
                    {error && (
                      <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                        <div className="flex items-center">
                          <SafeIcon icon={FiAlertCircle} className="h-4 w-4 mr-2" />
                          {error}
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                      {/* Left Column - Basic Info */}
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Appointment Type *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e)=> setFormData({...formData,name: e.target.value})}
                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="e.g.,General Consultation"
                            required
                          />
                        </div>

                        {/* Enable/Disable Toggle */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Availability Status
                          </label>
                          <div className="flex items-center space-x-3">
                            <button
                              type="button"
                              onClick={()=> setFormData({...formData,isEnabled: !formData.isEnabled})}
                              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                formData.isEnabled ? 'bg-green-600' : 'bg-gray-200'
                              }`}
                            >
                              <span
                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  formData.isEnabled ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                            <span className={`text-sm font-medium ${
                              formData.isEnabled ? 'text-green-700' : 'text-gray-500'
                            }`}>
                              {formData.isEnabled ? 'Enabled (Available for booking)' : 'Disabled (Hidden from booking)'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {formData.isEnabled 
                              ? 'This appointment type will be available for patients to book' 
                              : 'This appointment type will be hidden from the booking system'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Available Days *
                          </label>
                          <div className="grid grid-cols-2 gap-2">
                            {daysOfWeek.map((day)=> (
                              <button
                                key={day}
                                type="button"
                                onClick={()=> handleDayToggle(day)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                                  formData.availableDays.includes(day)
                                    ? 'bg-blue-100 text-blue-700 border-2 border-blue-300 shadow-sm'
                                    : 'bg-gray-100 text-gray-700 border-2 border-transparent hover:bg-gray-200'
                                }`}
                              >
                                {day.substring(0,3)}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Consultation Fee (₹) *
                          </label>
                          <input
                            type="number"
                            value={formData.consultationFee}
                            onChange={(e)=> setFormData({...formData,consultationFee: e.target.value})}
                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="500"
                            required
                            min="1"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Duration *
                          </label>
                          <div className="space-y-3">
                            {durationOptions.map((option)=> (
                              <label key={option.value} className="flex items-start">
                                <input
                                  type="radio"
                                  name="duration"
                                  value={option.value}
                                  checked={formData.duration===option.value}
                                  onChange={(e)=> setFormData({
                                    ...formData,
                                    duration: parseInt(e.target.value),
                                    selectedTimeSlots: [] // Reset time slots when duration changes
                                  })}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                                />
                                <div className="ml-3">
                                  <span className="text-sm font-medium text-gray-900">{option.label}</span>
                                  <p className="text-xs text-gray-500">{option.description}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Location *
                          </label>
                          <input
                            type="text"
                            value={formData.location}
                            onChange={(e)=> setFormData({...formData,location: e.target.value})}
                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Best Podiatrist Clinic,Mumbai"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Google Maps Embed URL *
                          </label>
                          <input
                            type="url"
                            value={formData.googleMapsUrl}
                            onChange={(e)=> setFormData({...formData,googleMapsUrl: e.target.value})}
                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="https://maps.google.com/embed?pb=..."
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Required: Enter the Google Maps embed URL for the appointment location
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Additional Information *
                          </label>
                          <textarea
                            value={formData.additionalInfo}
                            onChange={(e)=> setFormData({...formData,additionalInfo: e.target.value})}
                            rows={3}
                            className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Special instructions for patients..."
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Required: Please provide additional information or instructions
                          </p>
                        </div>
                      </div>

                      {/* Right Column - Time Slots Selection */}
                      <div className="xl:col-span-2">
                        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 h-full">
                          <div className="flex justify-between items-center mb-6">
                            <h4 className="text-lg font-semibold text-gray-900">
                              Select Time Slots (9 AM - 9 PM) *
                            </h4>
                            <div className="flex space-x-3">
                              <button
                                type="button"
                                onClick={handleSelectAllTimeSlots}
                                className="px-4 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium"
                              >
                                Select All
                              </button>
                              <button
                                type="button"
                                onClick={handleClearAllTimeSlots}
                                className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors font-medium"
                              >
                                Clear All
                              </button>
                            </div>
                          </div>

                          {formData.duration ? (
                            <div className="space-y-6">
                              <div className="bg-white rounded-lg p-4 text-sm text-gray-600">
                                <div className="grid grid-cols-3 gap-4">
                                  <div><strong>Duration:</strong> {getDurationLabel(formData.duration)}</div>
                                  <div><strong>Available Slots:</strong> {availableTimeSlots.length}</div>
                                  <div><strong>Selected Slots:</strong> <span className="text-blue-600 font-semibold">{formData.selectedTimeSlots.length}</span></div>
                                </div>
                              </div>

                              {(()=> {
                                const {morning,afternoon,evening}=groupTimeSlotsByPeriod(availableTimeSlots);
                                return (
                                  <div className="space-y-6 max-h-96 overflow-y-auto">
                                    {/* Morning Slots */}
                                    {morning.length > 0 && (
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                          <div className="h-2 w-2 bg-yellow-400 rounded-full mr-2"></div>
                                          Morning (9 AM - 12 PM) - {morning.filter(slot=> formData.selectedTimeSlots.includes(slot.value)).length}/{morning.length} selected
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          {morning.map((slot)=> (
                                            <button
                                              key={slot.id}
                                              type="button"
                                              onClick={()=> handleTimeSlotToggle(slot.value)}
                                              className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                                                formData.selectedTimeSlots.includes(slot.value)
                                                  ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105'
                                                  : 'bg-white text-gray-700 border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                                              }`}
                                            >
                                              {formData.selectedTimeSlots.includes(slot.value) && (
                                                <SafeIcon icon={FiCheck} className="inline h-3 w-3 mr-1" />
                                              )}
                                              {slot.display}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Afternoon Slots */}
                                    {afternoon.length > 0 && (
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                          <div className="h-2 w-2 bg-orange-400 rounded-full mr-2"></div>
                                          Afternoon (12 PM - 5 PM) - {afternoon.filter(slot=> formData.selectedTimeSlots.includes(slot.value)).length}/{afternoon.length} selected
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          {afternoon.map((slot)=> (
                                            <button
                                              key={slot.id}
                                              type="button"
                                              onClick={()=> handleTimeSlotToggle(slot.value)}
                                              className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                                                formData.selectedTimeSlots.includes(slot.value)
                                                  ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105'
                                                  : 'bg-white text-gray-700 border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                                              }`}
                                            >
                                              {formData.selectedTimeSlots.includes(slot.value) && (
                                                <SafeIcon icon={FiCheck} className="inline h-3 w-3 mr-1" />
                                              )}
                                              {slot.display}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Evening Slots */}
                                    {evening.length > 0 && (
                                      <div>
                                        <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                          <div className="h-2 w-2 bg-purple-400 rounded-full mr-2"></div>
                                          Evening (5 PM - 9 PM) - {evening.filter(slot=> formData.selectedTimeSlots.includes(slot.value)).length}/{evening.length} selected
                                        </h5>
                                        <div className="grid grid-cols-2 gap-3">
                                          {evening.map((slot)=> (
                                            <button
                                              key={slot.id}
                                              type="button"
                                              onClick={()=> handleTimeSlotToggle(slot.value)}
                                              className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                                                formData.selectedTimeSlots.includes(slot.value)
                                                  ? 'bg-blue-600 text-white border-blue-600 shadow-md transform scale-105'
                                                  : 'bg-white text-gray-700 border-gray-200 hover:bg-blue-50 hover:border-blue-300'
                                              }`}
                                            >
                                              {formData.selectedTimeSlots.includes(slot.value) && (
                                                <SafeIcon icon={FiCheck} className="inline h-3 w-3 mr-1" />
                                              )}
                                              {slot.display}
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="text-center py-12 text-gray-500">
                              <SafeIcon icon={FiClock} className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                              <p className="text-lg">Select a duration to see available time slots</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Modal Footer */}
                    <div className="flex justify-end space-x-4 pt-8 border-t border-gray-200 mt-8">
                      <button
                        type="button"
                        onClick={resetForm}
                        disabled={submitting}
                        className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                      >
                        Cancel
                      </button>
                      <motion.button
                        whileHover={{scale: submitting ? 1 : 1.02}}
                        whileTap={{scale: submitting ? 1 : 0.98}}
                        type="submit"
                        disabled={submitting}
                        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                      >
                        {submitting ? (
                          <div className="flex items-center">
                            <SafeIcon icon={FiLoader} className="animate-spin h-5 w-5 mr-2" />
                            {editingAppointment ? 'Updating...' : 'Creating...'}
                          </div>
                        ) : (
                          `${editingAppointment ? 'Update' : 'Create'} Appointment Type`
                        )}
                      </motion.button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentManagement;